import React, { useState, useEffect } from 'react';
import { router, Head } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import ParticleBackground from '../../components/ParticleBackground';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

export default function Shuffle({ reading, spread, cards, interpretation, meta_tags }) {
  const [selectedCards, setSelectedCards] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [revealedCards, setRevealedCards] = useState([]);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const handleCardClick = (card) => {
    if (isShuffling) return;

    if (selectedCards.find(c => c.id === card.id)) {
      // Deselect
      setSelectedCards(selectedCards.filter(c => c.id !== card.id));
    } else if (selectedCards.length < spread.card_count) {
      // Select
      const newSelected = [...selectedCards, card];
      setSelectedCards(newSelected);

      // If all cards selected, auto-shuffle after 0.5s
      if (newSelected.length === spread.card_count) {
        setTimeout(() => {
          startShuffleAndReveal(newSelected);
        }, 500);
      }
    }
  };

  const startShuffleAndReveal = (selected) => {
    setIsShuffling(true);

    // Submit card selection in background
    router.post(`/readings/${reading.uuid}/draw_cards`, {
      card_ids: selected.map(c => c.id)
    }, {
      preserveState: true,
      preserveScroll: true,
      only: []
    });

    // After 5 seconds, transition to reveal
    setTimeout(() => {
      setShowReveal(true);
    }, 5000);
  };

  const isCardSelected = (card) => selectedCards.some(c => c.id === card.id);

  // Display 20 cards in a semi-circular arrangement
  const displayedCards = cards.slice(0, 20);

  // Auto-reveal cards one by one
  useEffect(() => {
    if (showReveal && selectedCards.length > 0 && revealedCards.length < selectedCards.length) {
      const timer = setTimeout(() => {
        setRevealedCards(prev => [...prev, selectedCards[prev.length].id]);
      }, 800); // 800ms delay between each card reveal

      return () => clearTimeout(timer);
    }
  }, [showReveal, revealedCards.length, selectedCards]);

  // Show interpretation after all cards revealed
  useEffect(() => {
    if (revealedCards.length === selectedCards.length && selectedCards.length > 0) {
      const timer = setTimeout(() => {
        setShowInterpretation(true);
        setIsPolling(!interpretation);
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [revealedCards.length, selectedCards.length, interpretation]);

  // Handle card reveal on reveal screen (manual click - backup)
  const handleCardReveal = (cardId) => {
    if (!revealedCards.includes(cardId)) {
      setRevealedCards([...revealedCards, cardId]);
    }
  };

  const isCardRevealed = (cardId) => revealedCards.includes(cardId);
  const allCardsRevealed = revealedCards.length === selectedCards.length;

  // Poll for interpretation if not ready
  useEffect(() => {
    if (!isPolling || interpretation) {
      setIsPolling(false);
      return;
    }

    const pollInterval = setInterval(() => {
      router.reload({ only: ['interpretation'], preserveScroll: true });
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isPolling, interpretation]);

  // Stop polling when interpretation arrives
  useEffect(() => {
    if (interpretation && isPolling) {
      setIsPolling(false);
      if (allCardsRevealed) {
        setShowInterpretation(true);
      }
    }
  }, [interpretation, isPolling, allCardsRevealed]);

  // If showing reveal screen
  if (showReveal) {
    return (
      <>
        <Head>
          <title>Your Tarot Reading | Tarot-Reader.AI</title>
          <meta name="description" content="Discover the insights from your tarot card reading" />
          {meta_tags?.canonical && <link rel="canonical" href={meta_tags.canonical} />}
        </Head>

        <div className="bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 min-h-screen relative overflow-hidden flex flex-col">
        <ParticleBackground starCount={30} particleCount={15} />
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex-1 py-20">
          {/* Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Animated Icon */}
            <motion.div
              className="inline-block mb-6"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="relative">
                <span className="text-7xl drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]">🔮</span>
                <motion.div
                  className="absolute inset-0 blur-xl bg-amber-500/30 rounded-full"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
              </div>
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              <span className="bg-gradient-to-r from-amber-200 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                Your Reading
              </span>
            </h1>
            <p className="text-xl text-purple-100 mb-2">{reading.question}</p>
            <p className="text-amber-200">{spread.name}</p>
          </motion.div>

          {/* Instructions */}
          {!allCardsRevealed && (
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <p className="text-lg text-amber-300 font-semibold">
                Click each card to reveal its wisdom
              </p>
            </motion.div>
          )}

          {/* Cards - Horizontal Layout */}
          <div className="flex justify-center items-center gap-8 mb-12 flex-wrap">
            {selectedCards.map((card, index) => {
              const isRevealed = isCardRevealed(card.id);

              return (
                <motion.div
                  key={card.id}
                  className="flex flex-col items-center"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.3, duration: 0.6 }}
                >
                  {/* Card */}
                  <motion.div
                    className={`relative ${!isRevealed ? 'cursor-pointer' : 'cursor-default'}`}
                    style={{ width: '220px', height: '330px', perspective: '1000px' }}
                    onClick={() => !isRevealed && handleCardReveal(card.id)}
                    whileHover={!isRevealed ? { scale: 1.05 } : {}}
                    whileTap={!isRevealed ? { scale: 0.95 } : {}}
                  >
                    {/* Sparkle burst when card flips */}
                    {isRevealed && (
                      <motion.div
                        className="absolute inset-0 pointer-events-none"
                        style={{ zIndex: 100 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        {[...Array(12)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute bg-amber-400 rounded-full"
                            style={{
                              left: '50%',
                              top: '50%',
                              width: '8px',
                              height: '8px',
                            }}
                            initial={{ x: 0, y: 0, opacity: 1, scale: 0 }}
                            animate={{
                              x: Math.cos((i / 12) * Math.PI * 2) * 150,
                              y: Math.sin((i / 12) * Math.PI * 2) * 150,
                              opacity: 0,
                              scale: [0, 2, 0],
                            }}
                            transition={{
                              duration: 1.5,
                              ease: 'easeOut',
                            }}
                          />
                        ))}
                      </motion.div>
                    )}

                    <motion.div
                      className="relative w-full h-full"
                      style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
                      animate={{ rotateY: isRevealed ? 180 : 0 }}
                      transition={{ duration: 0.8, ease: [0.645, 0.045, 0.355, 1], type: 'spring', damping: 20 }}
                    >
                      {/* Card Back */}
                      <div
                        className="absolute inset-0 rounded-lg shadow-2xl overflow-hidden"
                        style={{
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-700 to-purple-900">
                          <div
                            className="absolute inset-0 opacity-20"
                            style={{
                              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                              backgroundSize: '20px 20px',
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-6xl mb-4 animate-pulse">🔮</div>
                              <div className="text-base text-purple-200 font-heading tracking-widest">
                                TAROT
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Card Front */}
                      <div
                        className="absolute inset-0 rounded-lg shadow-2xl overflow-hidden bg-white"
                        style={{
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                        }}
                      >
                        <img
                          src={`${card.image_path}`}
                          alt={card.full_name || card.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                          <h3 className="text-white font-heading text-base text-center font-semibold">
                            {card.full_name || card.name}
                          </h3>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>

                  {/* Position Label */}
                  <motion.div
                    className="mt-4 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.3 + 0.3 }}
                  >
                    <p className="text-sm text-gray-400 font-semibold">
                      {spread.positions?.[index]?.name || `Card ${index + 1}`}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* AI Interpretation */}
          <AnimatePresence>
            {showInterpretation && (
              <motion.div
                className="max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -50, scale: 0.9 }}
                transition={{ duration: 0.8, type: 'spring', stiffness: 100, damping: 15 }}
              >
                {interpretation ? (
                  <div className="relative">
                    {/* Glowing border effect */}
                    <motion.div
                      className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-30"
                      animate={{
                        opacity: [0.2, 0.4, 0.2],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                      }}
                    />

                    <div className="relative bg-gradient-to-br from-purple-900/90 to-indigo-900/90 backdrop-blur-xl p-8 rounded-3xl border-2 border-purple-400/30 shadow-2xl">
                      {/* Header */}
                      <motion.div
                        className="text-center mb-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <div className="text-4xl mb-3">✨</div>
                        <h2 className="text-2xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-2">
                          Your Reading Interpretation
                        </h2>
                        <div className="h-1 w-24 bg-gradient-to-r from-amber-400 to-purple-500 mx-auto rounded-full"></div>
                      </motion.div>

                      {/* Interpretation */}
                      <motion.div
                        className="prose prose-invert max-w-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                      >
                        <div
                          className="text-purple-100 leading-relaxed whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: interpretation }}
                        />
                      </motion.div>

                      {/* Action Buttons */}
                      <motion.div
                        className="mt-8 flex justify-center gap-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9 }}
                      >
                        <motion.a
                          href="/"
                          className="bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-semibold"
                          whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(251, 191, 36, 0.6)' }}
                          whileTap={{ scale: 0.95 }}
                        >
                          New Reading
                        </motion.a>
                      </motion.div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Loading message */}
                    <div className="text-center mb-8">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-400 mb-4"></div>
                      <p className="text-purple-100 text-lg">
                        The universe is preparing your interpretation...
                      </p>
                      <p className="text-purple-300 text-sm mt-2">
                        Meanwhile, explore your cards below
                      </p>
                    </div>

                    {/* Display cards while waiting */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {selectedCards.map((card, index) => (
                        <motion.div
                          key={card.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur rounded-2xl overflow-hidden border border-purple-400/30"
                        >
                          {/* Card Image */}
                          <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-800 to-indigo-900">
                            <img
                              src={`${card.image_path}`}
                              alt={card.full_name || card.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Card Info */}
                          <div className="p-4">
                            <div className="mb-2">
                              <span className="text-amber-300 text-xs font-semibold uppercase tracking-wider">
                                {spread.positions?.[index]?.name || `Card ${index + 1}`}
                              </span>
                            </div>
                            <h3 className="text-xl font-heading font-bold text-white mb-2">
                              {card.full_name || card.name}
                            </h3>
                            <p className="text-purple-200 text-sm leading-relaxed line-clamp-4">
                              {card.description || card.meaning || 'A powerful card in the tarot deck, offering deep insights and guidance for your journey.'}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Progress Indicator */}
          {!allCardsRevealed && (
            <motion.div
              className="fixed bottom-8 right-8 bg-gradient-to-br from-purple-900/90 to-indigo-900/90 backdrop-blur-xl p-4 rounded-lg border-2 border-purple-400/30 shadow-xl"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <p className="text-purple-300 text-sm mb-2">Cards Revealed</p>
              <p className="text-2xl font-heading font-bold text-amber-300">
                {revealedCards.length} / {selectedCards.length}
              </p>
            </motion.div>
          )}
        </div>

        <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Choose Your Cards | Tarot Reading</title>
        <meta name="description" content="Select your tarot cards for your personalized reading" />
        {meta_tags?.canonical && <link rel="canonical" href={meta_tags.canonical} />}
      </Head>

      {/* CSS Animations for performance */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes pulse-glow {
          0%, 100% {
            border-color: rgba(251, 191, 36, 0.2);
            box-shadow: 0 0 20px rgba(251, 191, 36, 0.2);
          }
          50% {
            border-color: rgba(251, 191, 36, 0.5);
            box-shadow: 0 0 30px rgba(251, 191, 36, 0.4);
          }
        }
        @keyframes pulse-ring {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.15); opacity: 0; }
        }
      `}</style>

      <div className="bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 min-h-screen relative overflow-hidden flex flex-col">
        <ParticleBackground />
        <Header />

        <div className="relative z-10 flex-1 py-8 md:py-16">
          {/* Top Section - Question and Counter */}
          <motion.div
            className="text-center mb-4 md:mb-8 px-4 max-w-7xl mx-auto"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-sm md:text-base text-purple-200 mb-3">Thinking about your question</p>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 md:mb-8 leading-tight max-w-4xl mx-auto">
              <span className="bg-gradient-to-r from-amber-200 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                {reading.question}
              </span>
            </h1>

            {/* Selection Counter - At Top - Larger */}
            <motion.div
              className="inline-flex items-center justify-center mb-2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: 'spring' }}
            >
              <div className="relative">
                <svg className="w-32 h-32 md:w-40 md:h-40" viewBox="0 0 120 120">
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="rgba(168, 85, 247, 0.2)"
                    strokeWidth="6"
                  />
                  <motion.circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="url(#gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: selectedCards.length / spread.card_count }}
                    transition={{ duration: 0.5 }}
                    style={{
                      transformOrigin: 'center',
                      transform: 'rotate(-90deg)',
                      willChange: 'stroke-dashoffset',
                    }}
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fbbf24" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.div
                    className="text-4xl md:text-5xl font-heading font-bold text-amber-200"
                    key={selectedCards.length}
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {selectedCards.length}
                  </motion.div>
                  <div className="text-sm md:text-base text-purple-200">of {spread.card_count}</div>
                </div>
              </div>
            </motion.div>

            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold">
              <span className="bg-gradient-to-r from-amber-300 to-purple-400 bg-clip-text text-transparent">
                Select · {selectedCards.length < spread.card_count ? spread.card_count - selectedCards.length : spread.card_count} · cards
              </span>
            </h2>
          </motion.div>

          {/* Shuffle Animation Overlay */}
          <AnimatePresence>
            {isShuffling && (
              <motion.div
                className="fixed inset-0 bg-indigo-950/95 z-50 flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center">
                  <motion.div
                    className="text-8xl mb-6"
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: [0, 180, 360],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    🔮
                  </motion.div>
                  <motion.h2
                    className="text-3xl font-heading font-bold mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className="bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text text-transparent">
                      Shuffling the Cards...
                    </span>
                  </motion.h2>
                  <motion.p
                    className="text-purple-200 text-lg"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    The universe is aligning your reading
                  </motion.p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        {/* Card Spread - Semi-circular responsive */}
        {(() => {
          const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
          const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 768;

          return (
            <div className={`relative w-full -mt-12 mb-4 ${isMobileView ? 'px-0' : 'max-w-7xl mx-auto px-4'}`} style={{
              height: isMobileView ? '280px' : '320px',
              overflow: isMobileView ? 'hidden' : 'visible',
            }}>
              {displayedCards.map((card, index) => {
                const selected = isCardSelected(card);
                const disabled = !selected && selectedCards.length >= spread.card_count;

                // Mobile: show more cards for better visibility
                const isMobile = isMobileView;
            const visibleRange = isMobile ? 12 : displayedCards.length;
            const startIdx = isMobile ? Math.floor((displayedCards.length - visibleRange) / 2) : 0;
            const isVisible = !isMobile || (index >= startIdx && index < startIdx + visibleRange);

            // Semi-circle calculation - responsive based on viewport
            const startAngle = isMobile ? Math.PI * 0.05 : Math.PI * 0.08;
            const endAngle = isMobile ? Math.PI * 0.95 : Math.PI * 0.92;
            const angle = startAngle + ((endAngle - startAngle) * index) / (displayedCards.length - 1);

            // Responsive radius - use viewport width for full width on mobile
            const baseRadius = isMobile ? (viewportWidth / 2) : 380;
            const radius = baseRadius;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius * (isMobile ? 0.35 : 0.45);
            const rotation = (angle * 180) / Math.PI - 90;

            // Responsive card size
            const cardWidth = isMobile ? 70 : 120;
            const cardHeight = isMobile ? 105 : 180;

            // Only render visible cards on mobile
            if (isMobile && !isVisible) return null;

            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, scale: 0.5, x: 0, y: 0, rotate: 0 }}
                animate={isShuffling ? {
                  opacity: 1,
                  scale: [1, 0.8, 1.2, 0.9, 1],
                  x: [x, x + Math.random() * 100 - 50, x - Math.random() * 100 + 50, x],
                  y: [y, y + Math.random() * 100 - 50, y - Math.random() * 100 + 50, y],
                  rotate: [rotation, rotation + Math.random() * 360, rotation - Math.random() * 360, rotation],
                } : {
                  opacity: 1,
                  scale: 1,
                  x: x,
                  y: y,
                  rotate: rotation
                }}
                transition={
                  isShuffling
                    ? { delay: index * 0.05, duration: 5, repeat: Infinity, ease: 'easeInOut' }
                    : { delay: index * 0.03, duration: 0.6, type: 'spring', stiffness: 100 }
                }
                whileHover={!disabled && !isShuffling ? { scale: 1.15, zIndex: 100, rotate: 0 } : {}}
                whileTap={!disabled && !isShuffling ? { scale: 0.95 } : {}}
                onClick={() => !disabled && !isShuffling && handleCardClick(card)}
                className={`absolute cursor-pointer ${disabled || isShuffling ? 'opacity-70 cursor-not-allowed' : ''}`}
                style={{
                  width: `${cardWidth}px`,
                  height: `${cardHeight}px`,
                  left: '50%',
                  top: '55%',  // Slightly lower to account for flatter arc
                  marginLeft: `-${cardWidth/2}px`,
                  marginTop: `-${cardHeight/2}px`,
                  zIndex: selected ? 50 : 10,
                  willChange: 'transform',
                }}
              >
                {/* Card */}
                <div
                  className={`relative w-full h-full rounded-xl shadow-2xl transition-all duration-300 ${
                    selected
                      ? 'ring-4 ring-amber-400 shadow-amber-500/80'
                      : 'hover:shadow-amber-500/40'
                  }`}
                  style={{
                    boxShadow: selected
                      ? '0 0 40px rgba(251, 191, 36, 0.8), 0 20px 60px rgba(0, 0, 0, 0.6)'
                      : '0 10px 40px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  {/* Card Back - Mystical Purple Design */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 rounded-xl border-[6px] border-amber-500">
                    {/* Subtle decorative pattern */}
                    <div
                      className="absolute inset-0 opacity-5 pointer-events-none"
                      style={{
                        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                        backgroundSize: '30px 30px',
                      }}
                    />


                    {/* TAROT text */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-[10px] sm:text-xs text-purple-100 font-heading tracking-[0.3em] sm:tracking-[0.4em]">
                        TAROT
                      </div>
                    </div>

                    {/* Corner stars */}
                    <div className="absolute top-3 left-3 text-amber-300/80 pointer-events-none text-lg">
                      ✦
                    </div>
                    <div className="absolute top-3 right-3 text-amber-300/80 pointer-events-none text-lg">
                      ✦
                    </div>
                    <div className="absolute bottom-3 left-3 text-amber-300/80 pointer-events-none text-lg">
                      ✦
                    </div>
                    <div className="absolute bottom-3 right-3 text-amber-300/80 pointer-events-none text-lg">
                      ✦
                    </div>
                  </div>

                  {/* Selection Glow & Number */}
                  {selected && (
                    <motion.div
                      className="absolute inset-0 bg-amber-400/30 backdrop-blur-[2px] flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        className="bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 text-white rounded-full w-14 h-14 flex items-center justify-center font-bold text-2xl shadow-xl"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                      >
                        {selectedCards.findIndex(c => c.id === card.id) + 1}
                      </motion.div>
                    </motion.div>
                  )}
                </div>

                {/* Optimized sparkle effect on selection */}
                {selected && !isShuffling && (
                  <>
                    {/* Single pulsing glow ring - CSS animation */}
                    <div
                      className="absolute inset-0 pointer-events-none rounded-xl"
                      style={{
                        border: '3px solid rgba(251, 191, 36, 0.6)',
                        animation: 'pulse-ring 2s ease-in-out infinite',
                      }}
                    />

                    {/* Reduced twinkling stars */}
                    {[0, 2, 4].map((i) => (
                      <motion.div
                        key={`star-${i}`}
                        className="absolute text-amber-300 pointer-events-none"
                        style={{
                          left: `${50 + Math.cos((i / 6) * Math.PI * 2) * 60}%`,
                          top: `${50 + Math.sin((i / 6) * Math.PI * 2) * 50}%`,
                          fontSize: '16px',
                          transform: 'translate(-50%, -50%)',
                          willChange: 'transform, opacity',
                        }}
                        animate={{
                          opacity: [0, 1, 0],
                          scale: [0, 1.3, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.4,
                          ease: 'easeInOut',
                        }}
                      >
                        ✨
                      </motion.div>
                    ))}
                  </>
                )}
              </motion.div>
            );
          })}
            </div>
          );
        })()}
        </div>

        <Footer />
      </div>
    </>
  );
}